<template>
  <div class="text-start">
    <!--trainingModule title-->
    <v-row>
      <v-col class="pb-0">
        <span class="subtitle-2 primary--text">
          {{ $t("course.module") }}
          {{ trainingProgress.trainingModuleIndex + 1 }}:
          {{ getLocalisedValue(trainingProgress.trainingModule.title) }}
        </span>
      </v-col>
    </v-row>

    <!--trainingModuleContent title-->
    <v-row>
      <v-col class="pt-0">
        <span class="textLight--text font-weight-bold text-start text-h4 mb-1">
          {{
            getTrainingModuleContentTitle(
              trainingProgress.trainingModuleContent
            )
          }}
        </span>
      </v-col>
    </v-row>

    <!--trainingModuleContent content QUIZ time out-->
    <template v-if="isQuizContent">
      <v-row>
        <v-col class="py-0">
          <span class="text-h6 font-weight-medium primary--text">
            {{ $t("course.quiz") }}:
            {{ quizTimeOut }}
            {{ $t("course.min") }}
          </span>
        </v-col>
      </v-row>
    </template>

    <!--trainingModuleContent content QUIZ running-->
    <template v-if="isQuizRunning">
      <!--trainingModuleContent quiz question card-->
      <v-row
        v-for="(quizQuestionAnswer, index) in quizQuestionAnswers"
        :key="'quizQuestionAnswer-' + index"
        align="start"
      >
        <v-col cols="12">
          <v-card flat class="rounded-2">
            <div class="d-flex pa-4 body-1 textLight--text text-start">
              <span class="pe-4"> {{ index + 1 }}. </span>

              <div class="flex-grow-1">
                <p class="ma-0">
                  {{ getLocalisedValue(quizQuestionAnswer.question) }}
                </p>

                <v-radio-group
                  v-model="quizQuestionAnswer.givenAnswerKey"
                  hide-details
                  class="pb-2"
                >
                  <v-radio
                    v-for="(
                      quizQuestionAnswerChoice, index
                    ) in quizQuestionAnswer.choices"
                    :key="'quizQuestionAnswerChoice-' + index"
                    :label="getLocalisedValue(quizQuestionAnswerChoice.value)"
                    :value="quizQuestionAnswerChoice.key"
                    :disabled="!isQuizChoiceSelectionEnabled"
                  />
                </v-radio-group>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!--trainingModuleContent quiz mark complete button-->
      <v-row class="my-10">
        <v-col>
          <v-btn
            :disabled="isMarkAsCompleteDisabledForQuiz"
            depressed
            class="primary caption text-capitalize py-5 rounded-2"
            :loading="loading"
            @click="markAsComplete(true)"
          >
            <v-icon size="14" class="me-1"> mdi-check </v-icon>

            {{ $t("course.markComplete") }}
          </v-btn>
        </v-col>
      </v-row>
    </template>

    <template v-else>
      <!--trainingModuleContent content AUDIO-->
      <template v-if="isAudioContent">
        <v-row
          v-if="hasContents(trainingProgress.trainingModuleContent.audios)"
        >
          <v-col>
            <AudioPlayer
              :mediaUrl="
                getVideo(trainingProgress.trainingModuleContent.audios)
              "
              :title="
                getLocalisedValue(trainingProgress.trainingModuleContent.title)
              "
            />
          </v-col>
        </v-row>
      </template>

      <!--trainingModuleContent content VIDEO-->
      <template v-else-if="isVideoContent">
        <v-row
          v-if="hasContents(trainingProgress.trainingModuleContent.videos)"
        >
          <v-col>
            <VideoPlayer
              :mediaUrl="
                getVideo(trainingProgress.trainingModuleContent.videos)
              "
            />
            <!--          @onEnd="-->
            <!--          trainingProgress.status === 'ON_GOING' ? markAsComplete() : null-->
            <!--          "-->
          </v-col>
        </v-row>
      </template>

      <!--trainingModuleContent description-->
      <v-row
        v-if="
          !isTextContent &&
          hasContents(trainingProgress.trainingModuleContent.description)
        "
        class="mb-6"
      >
        <v-col>
          <span
            class="body-1 textLight--text text-start"
            v-html="
              getLocalisedValue(
                trainingProgress.trainingModuleContent.description
              )
            "
          />
        </v-col>
      </v-row>

      <!--trainingModuleContent content TEXT html-->
      <v-row v-if="isTextContent" class="my-6">
        <v-col>
          <span
            v-if="hasContents(trainingProgress.trainingModuleContent.text)"
            class="body-1 textLight--text text-start"
            v-html="
              getLocalisedValue(trainingProgress.trainingModuleContent.text)
            "
          />
        </v-col>
      </v-row>

      <!--trainingModuleContent content QUIZ start & history-->
      <template v-if="isQuizContent">
        <!--trainingModuleContent quiz timed warning card-->
        <v-row v-if="canRetryQuiz">
          <v-col cols="12" sm="10" md="11" lg="9">
            <v-card
              flat
              class="rounded-2"
              style="border-left: solid 12px rgba(89, 40, 229, 1)"
            >
              <div class="d-inline-flex pa-4">
                <v-icon size="42" color="rgba(28, 27, 31, 1)" class="pe-4">
                  mdi-timer-outline
                </v-icon>

                <div class="text-start textLight--text">
                  <p class="text-h6 font-weight-medium ma-0">
                    {{ $t("course.timedQuizTitle") }}
                  </p>

                  <p class="caption ma-0">
                    <span style="opacity: 0.7">
                      {{ $t("course.timedQuizBody1") }}
                    </span>

                    <span class="font-weight-bold">
                      {{ quizTimeOut }}
                      {{ $t("course.minutes").toLowerCase() }}
                    </span>

                    <span style="opacity: 0.7">
                      {{ $t("course.timedQuizBody2") }}
                    </span>
                  </p>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!--trainingModuleContent quiz start button-->
        <v-row v-if="canRetryQuiz">
          <v-col>
            <v-btn
              depressed
              class="
                primary
                caption
                text-capitalize
                py-5
                rounded-2
                text-uppercase
              "
              min-width="130"
              @click="startQuiz"
            >
              {{ $t("course.start") }}
            </v-btn>
          </v-col>
        </v-row>

        <!--trainingModuleContent quiz history-->
        <template v-if="hasContents(trainingProgress.quizAttempts)">
          <!--trainingModuleContent quiz history title-->
          <v-row no-gutters class="mt-8">
            <v-col>
              <span class="text-h6 font-weight-medium textLight--text">
                {{ $t("course.yourPreviousAttempts") }}
              </span>
            </v-col>
          </v-row>

          <!--trainingModuleContent quiz history attempts-->
          <v-row
            v-for="(quizAttempt, index) in trainingProgress.quizAttempts"
            :key="'quizAttempt-' + index"
            class="mb-3"
          >
            <v-col cols="12" sm="10" lg="8">
              <v-card
                flat
                rounded
                class="subtitle-2 textLight--text rounded-2 px-2 py-3"
              >
                <v-row>
                  <!--trainingModuleContent quiz history attempt date-->
                  <v-col cols="6" sm="4" class="py-1 text-start">
                    <span style="opacity: 0.7">
                      {{
                        moment(parseInt(quizAttempt.attemptedAt)).format(
                          "DD MMM yyyy"
                        )
                      }}
                    </span>
                  </v-col>

                  <!--trainingModuleContent quiz history attempt percentage-->
                  <v-col
                    cols="6"
                    sm="4"
                    md="3"
                    class="py-1 text-end text-sm-start"
                  >
                    <span>
                      {{
                        (
                          ((quizAttempt.acquiredMark || 0) /
                            (quizAttempt.totalMark || 1)) *
                          100
                        ).toFixed(0)
                      }}%
                    </span>
                  </v-col>

                  <!--trainingModuleContent quiz history attempt status-->
                  <v-col cols="12" sm="4" md="5">
                    <v-row>
                      <v-col cols class="py-1 text-start">
                        <v-icon
                          size="18"
                          :color="
                            quizAttempt.status === 'PASSED'
                              ? 'success'
                              : 'error'
                          "
                          class="me-1"
                        >
                          {{
                            quizAttempt.status === "PASSED"
                              ? "mdi-check-circle-outline"
                              : "mdi-close"
                          }}
                        </v-icon>

                        <span style="opacity: 0.7">
                          {{ quizAttempt.status }}
                        </span>
                      </v-col>

                      <v-col
                        cols="auto"
                        class="py-1 text-end hand-cursor"
                        @click="downloadAttemptPdf(quizAttempt)"
                      >
                        <v-icon size="18"> mdi-download </v-icon>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
        </template>

        <v-row class="mb-10" />
      </template>

      <!--trainingModuleContent mark complete button !isVideoContent && -->
      <v-row v-else-if="trainingProgress.status === 'ON_GOING'" class="mb-10">
        <v-col>
          <v-btn
            depressed
            class="primary caption text-capitalize py-5 rounded-2"
            :loading="loading"
            @click="markAsComplete"
          >
            <v-icon size="14" class="me-1"> mdi-check </v-icon>

            {{ $t("course.markComplete") }}
          </v-btn>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import VideoPlayer from "../../core-module/components/VideoPlayer";
import AudioPlayer from "../../core-module/components/AudioPlayer";

import { updateTrainingProgress } from "../methods/updateTrainingProgress";
import { bulkDownloadQuizAttempts } from "@/app/quiz-module/helpers/quizPdfHelper";

export default {
  name: "CourseModuleContent",
  components: { VideoPlayer, AudioPlayer },
  props: {
    trainingRegistration: {
      type: Object,
      required: true,
    },
    selectedTrainingProgress: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      trainingProgress: this.selectedTrainingProgress,
      isQuizRunning: false,
      isQuizChoiceSelectionEnabled: false,
      quizQuestionAnswers: [],

      subscriberInfo: null,
    };
  },
  computed: {
    isAudioContent() {
      return (
        this.trainingProgress.trainingModuleContent.contentType === "AUDIO"
      );
    },
    isVideoContent() {
      return (
        this.trainingProgress.trainingModuleContent.contentType === "VIDEO"
      );
    },
    isTextContent() {
      return this.trainingProgress.trainingModuleContent.contentType === "TEXT";
    },
    isQuizContent() {
      return this.trainingProgress.trainingModuleContent.contentType === "QUIZ";
    },
    quizTimeOut() {
      return this.trainingProgress?.trainingModuleContent?.quizContent?.quiz
        ?.timeOut;
    },
    canRetryQuiz() {
      const retryCount = this.trainingProgress.retryCount ?? 1;

      return (
        this.trainingRegistration.status === "STARTED" &&
        this.trainingProgress.status === "ON_GOING" &&
        (!this.trainingProgress.quizAttempts?.length ||
          this.trainingProgress.quizAttempts.length < retryCount)
      );
    },
    isMarkAsCompleteDisabledForQuiz() {
      return this.quizQuestionAnswers.some(x => x.givenAnswerKey == null);
    },
  },
  apollo: {
    getSubscriberProfile() {
      return {
        query: require("../../user-module/api/query/getSubscriberProfile.gql"),
        result({ data }) {
          if (data?.getSubscriberProfile) {
            this.subscriberInfo = data.getSubscriberProfile;
          }
        },
      };
    },
  },
  watch: {
    selectedTrainingProgress(newValue, oldValue) {
      if (newValue._id.toString() !== oldValue._id.toString()) {
        this.trainingProgress = this.selectedTrainingProgress;
        this.resetQuizVariables();
      }
    },
  },
  methods: {
    resetQuizVariables() {
      this.isQuizRunning = false;
      this.isQuizChoiceSelectionEnabled = false;
      this.quizQuestionAnswers = [];

      this.$emit("onQuizTimerUpdate", {
        isQuizRunning: this.isQuizRunning,
      });
    },
    startQuiz() {
      this.isQuizRunning = true;
      this.isQuizChoiceSelectionEnabled = true;
      const questionAnswers =
        this.trainingProgress.trainingModuleContent?.quizContent?.quiz
          ?.questionAnswers;
      const questionsDisplayedCount =
        this.trainingProgress.trainingModuleContent?.quizContent?.quiz
          ?.questionsDisplayedCount ?? 0;

      this.quizQuestionAnswers = this.getRandomSubArray(
        questionAnswers,
        questionsDisplayedCount
      )
        // ?.sort((a, b) => a.displayPosition - b.displayPosition)
        ?.map(x => {
          return {
            ...x,
            questionId: x._id,
            givenAnswerKey: null,
          };
        });

      if (this.quizTimeOut) {
        let availableTimeMillis = this.quizTimeOut * 60000;

        this.$emit("onQuizTimerUpdate", {
          isQuizRunning: this.isQuizRunning,
          duration: this.$moment.utc(availableTimeMillis).format("mm:ss"),
        });

        const quizTimer = setInterval(() => {
          availableTimeMillis -= 1000;

          if (availableTimeMillis <= 0 || !this.isQuizRunning) {
            this.isQuizChoiceSelectionEnabled = false;
            clearInterval(quizTimer);
          }

          this.$emit("onQuizTimerUpdate", {
            isQuizRunning: this.isQuizRunning,
            duration: this.$moment.utc(availableTimeMillis).format("mm:ss"),
          });
        }, 1000);
      }
    },
    downloadAttemptPdf(quizAttempt) {
      bulkDownloadQuizAttempts({
        quizAttempts: [
          {
            ...quizAttempt,
            title: this.getTrainingModuleContentTitle(
              this.trainingProgress.trainingModuleContent,
              "en"
            ),
          },
        ],
        subscriberInfo: this.subscriberInfo,
      });
    },
    markAsComplete(isQuiz = false) {
      if (!this.loading) {
        // currentTrainingModuleContentLastAccessedItem
        // currentTrainingModuleContentLastAccessedDuration
        // currentTrainingModuleContentQuizGrade

        let trainingRegistrationStatus,
          trainingRegistrationProgressPercentage,
          currentTrainingModuleContentStatus,
          currentTrainingModuleContentQuestionAnswers,
          nextTrainingModuleId,
          nextTrainingModuleContentId;

        if (
          this.trainingProgress.trainingModuleContentIndex + 1 <
          this.trainingProgress.trainingModule.trainingModuleContents.length
        ) {
          nextTrainingModuleContentId =
            this.trainingProgress.trainingModule.trainingModuleContents[
              this.trainingProgress.trainingModuleContentIndex + 1
            ]._id;
        }

        if (!nextTrainingModuleContentId) {
          if (
            this.trainingProgress.trainingModuleIndex + 1 <
            this.trainingRegistration.training.trainingModules.length
          ) {
            const nextTrainingModule =
              this.trainingRegistration.training.trainingModules[
                this.trainingProgress.trainingModuleIndex + 1
              ];

            nextTrainingModuleId = nextTrainingModule._id;

            if (nextTrainingModule.trainingModuleContents?.length) {
              nextTrainingModuleContentId =
                nextTrainingModule.trainingModuleContents[0]._id;
            }
          }
        }

        if (
          !nextTrainingModuleContentId &&
          this.trainingRegistration.status === "STARTED"
        ) {
          trainingRegistrationStatus = "COMPLETED";
          trainingRegistrationProgressPercentage = 100;
        }

        if (this.trainingProgress.status === "ON_GOING") {
          currentTrainingModuleContentStatus = "COMPLETED";
          trainingRegistrationProgressPercentage =
            this.getTrainingProgressPercentage(this.trainingRegistration, true);
        }

        if (isQuiz && this.quizQuestionAnswers.length) {
          currentTrainingModuleContentQuestionAnswers =
            this.quizQuestionAnswers.map(x => {
              return {
                questionId: x._id,
                givenAnswerKey: x.givenAnswerKey,
              };
            });
        }

        this.updateTrainingProgress(
          {
            trainingRegistrationStatus,
            trainingRegistrationProgressPercentage,
            currentTrainingModuleContentStatus,
            currentTrainingModuleContentQuestionAnswers,
            nextTrainingModuleId,
            nextTrainingModuleContentId,
          },
          isQuiz && this.trainingProgress.status === "COMPLETED"
        );
      }
    },
    updateTrainingProgress(input, refresh = false) {
      if (!this.loading) {
        this.loading = true;

        input = {
          trainingRegistrationId: this.trainingRegistration._id,
          currentTrainingModuleContentId:
            this.trainingProgress.trainingModuleContent._id,
          ...input,
        };

        updateTrainingProgress(input).then(response => {
          if (response.data) {
            let quizAttempt;

            if (input.currentTrainingModuleContentStatus === "COMPLETED") {
              const currentTrainingProgress =
                response.data.trainingProgresses.find(
                  x => x._id.toString() === this.trainingProgress._id.toString()
                );

              const nextTrainingProgress =
                response.data.trainingProgresses.find(
                  x => x.status === "ON_GOING"
                );

              if (
                nextTrainingProgress &&
                nextTrainingProgress._id.toString() ===
                  this.trainingProgress._id.toString()
              ) {
                this.trainingProgress = {
                  ...this.trainingProgress,
                  quizAttempts: nextTrainingProgress.quizAttempts,
                };
              } else {
                if (response.data.status === "COMPLETED") {
                  this.$emit("onTrainingCompleted", "CERTIFICATE");
                } else {
                  this.$emit("onModuleContentCompleted", nextTrainingProgress);
                }
              }

              if (currentTrainingProgress?.quizAttempts?.length)
                quizAttempt =
                  currentTrainingProgress.quizAttempts[
                    currentTrainingProgress?.quizAttempts?.length - 1
                  ];
            } else if (refresh) {
              const updatedTrainingProgress =
                response.data.trainingProgresses.find(
                  x => x._id.toString() === this.trainingProgress._id.toString()
                );

              if (updatedTrainingProgress) {
                this.trainingProgress = {
                  ...this.trainingProgress,
                  quizAttempts: updatedTrainingProgress.quizAttempts,
                };
              }

              if (updatedTrainingProgress?.quizAttempts?.length)
                quizAttempt =
                  updatedTrainingProgress.quizAttempts[
                    updatedTrainingProgress?.quizAttempts?.length - 1
                  ];
            }

            if (quizAttempt) this.downloadAttemptPdf(quizAttempt);

            this.resetQuizVariables();
          } else if (response.error) {
            this.$emit("onError");
          }

          this.loading = false;
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped />
